/**
 * The Museum's collection: a hall that grows as dinosaurs are excavated
 * (docs/mockups/museum-collection.html, B).
 *
 * Every form is its own species, a tier and its evolved form alike, and gets
 * an exhibit the first time it is dug up. Digging the same species up again
 * replaces its exhibit: the name and era shown are the latest dig's, while
 * every dig keeps its plaque in the History. Exhibits stand in roster order,
 * each evolved form right after its tier, with no empty plinths: what is still
 * to find is told by the count, not by a hall of dark cases.
 *
 * It used to be fifteen fixed plinths, one per tier, with an evolved form
 * standing on its tier's plinth; that left no room for both forms, and a new
 * player's museum was mostly empty.
 */

import { DINOS } from '../data/dinos';
import { mountUrl } from './sprites';
import { bindTooltip } from './tooltip';
import type { TooltipLine } from './tooltip';
import type { GameState } from '../game/state';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Every species the hall can hold: each tier and its evolved form. */
export const SPECIES_TOTAL = DINOS.length * 2;

export interface Exhibit {
  /** The sprite id, which is the species: a tier's own or its evolved form's. */
  sprite: string;
  /** The species' name. */
  species: string;
  /** The name the latest one dug up wore, if it had one. */
  name: string | null;
  /** The era it was most recently taken from. */
  era: number;
  /** How many eras it has been dug out of. */
  times: number;
}

/** What the hall holds, in roster order: each tier, then its evolved form. */
export function exhibits(state: GameState): Exhibit[] {
  const bySprite = new Map<string, Exhibit>();
  for (const row of state.eraLog) {
    if (row.excavated === null) continue;
    const def = DINOS.find((d) => d.id === row.excavated);
    if (!def) continue;
    const sprite = row.excavatedSprite === def.evolved.id ? def.evolved.id : def.id;
    const had = bySprite.get(sprite);
    // The latest dig takes the exhibit; the era log is in order, but a later
    // row is not assumed to be a later era.
    if (had && row.era < had.era) {
      had.times++;
      continue;
    }
    bySprite.set(sprite, {
      sprite,
      species: sprite === def.id ? def.name : def.evolved.name,
      name: row.excavatedName,
      era: row.era,
      times: (had?.times ?? 0) + 1,
    });
  }
  const out: Exhibit[] = [];
  for (const def of DINOS) {
    for (const sprite of [def.id, def.evolved.id]) {
      const found = bySprite.get(sprite);
      if (found) out.push(found);
    }
  }
  return out;
}

function buildExhibit(e: Exhibit): { node: HTMLElement; tip: TooltipLine[] } {
  const node = el('div', 'plinth');
  const img = new Image();
  img.className = 'plinth__mount';
  img.alt = '';
  img.src = mountUrl(e.sprite);
  node.append(
    img,
    el('span', 'plinth__label', e.species),
    el('span', 'plinth__who', e.name ? `${e.name} · Era ${e.era}` : `Era ${e.era}`),
  );
  const times = e.times === 1 ? 'Excavated once' : `Excavated ${e.times} times`;
  const tip: TooltipLine[] = [
    { text: e.species },
    { text: `${times}, last in era ${e.era}`, muted: true },
  ];
  bindTooltip(node, () => tip);
  return { node, tip };
}

export interface CollectionView {
  readonly element: HTMLElement;
  update(state: GameState): void;
  /** How many species are on show, for the drawer's count. */
  readonly filled: number;
}

/** Just what the hall shows, to tell whether it has to be rebuilt. */
function keyOf(list: readonly Exhibit[]): string {
  return list.map((e) => `${e.sprite}:${e.era}:${e.times}:${e.name ?? ''}`).join('|');
}

export function createCollectionView(): CollectionView {
  const wrap = el('div', 'collection');
  const summary = el('p', 'collection__summary');
  const grid = el('div', 'collection__grid');
  wrap.append(summary, grid);

  let lastKey = '';
  let filled = 0;

  return {
    element: wrap,
    get filled() {
      return filled;
    },
    update(state) {
      const list = exhibits(state);
      // Only when something was dug up: the panel refreshes ten times a
      // second, and rebuilding (and rebinding tooltips) each time would churn.
      const key = keyOf(list);
      if (key === lastKey) return;
      lastKey = key;
      filled = list.length;
      grid.replaceChildren(...list.map((e) => buildExhibit(e).node));
      summary.textContent = `${filled} of ${SPECIES_TOTAL} species`;
    },
  };
}
